<script setup>
import { ref, watch, onMounted } from 'vue';
import { useDebounce } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import twentyApi from 'dashboard/api/integrations/twenty';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  // linkedPersonId: { type: String, default: '' },
  // linkedOpportunities: { type: Array, default: () => [] },
});

const emit = defineEmits(['linked', 'close']);

const { t } = useI18n();

const STAGES = [
  'NEW',
  'SCREENING',
  'MEETING',
  'PROPOSAL',
  'CUSTOMER',
  'CHURNED',
  'LOST',
];

const inputStyles = { borderRadius: '0.75rem', fontSize: '0.875rem' };

const OPPORTUNITY_URL_RE =
  /^https?:\/\/.+\/object\/opportunity\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

const selectedTabIndex = ref(0);
const tabs = ref([
  {
    key: 0,
    name: t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.TAB_CREATE'),
  },
  {
    key: 1,
    name: t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.TAB_LINK'),
  },
]);

// // Derive the company of the first linked opportunity to filter further links
// const linkedCompanyId = computed(() => {
//   if (props.linkedPersonId) return '';
//   return props.linkedOpportunities[0]?.twenty_record_data?.company_id || '';
// });

// ─── link tab ────────────────────────────────────────────────────────────────
const searchQuery = ref('');
const debouncedQuery = useDebounce(searchQuery, 400);
const searchResults = ref([]);
const isSearching = ref(false);
const isLinking = ref(false);
const urlValidatedOpportunity = ref(null);
const urlError = ref('');
const isValidatingUrl = ref(false);

const opportunityDisplayName = record =>
  record.company
    ? `${record.company} | ${record.subtitle || ''}`
    : record.subtitle || '';

const validateOpportunityUrl = async url => {
  const match = url.match(OPPORTUNITY_URL_RE);
  if (!match) {
    urlError.value = t(
      'INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.INVALID_OPPORTUNITY_URL'
    );
    urlValidatedOpportunity.value = null;
    return;
  }
  urlError.value = '';
  urlValidatedOpportunity.value = null;
  isValidatingUrl.value = true;
  try {
    const { data } = await twentyApi.getOpportunity(match[1]);
    if (data?.data) {
      urlValidatedOpportunity.value = data.data;
    } else {
      urlError.value = t(
        'INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.OPPORTUNITY_NOT_FOUND'
      );
    }
  } catch {
    urlError.value = t(
      'INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.OPPORTUNITY_NOT_FOUND'
    );
  } finally {
    isValidatingUrl.value = false;
  }
};

const runSearch = async query => {
  urlValidatedOpportunity.value = null;
  urlError.value = '';
  if (OPPORTUNITY_URL_RE.test(query)) {
    searchResults.value = [];
    await validateOpportunityUrl(query);
    return;
  }
  isSearching.value = true;
  try {
    let response;
    // // TODO: change the logic so that search opportunities will return only opportunities
    // // for certain person if there is already person linked
    // if (props.linkedPersonId) {
    //   response = await twentyApi.searchOpportunitiesByPerson(
    //     props.linkedPersonId,
    //     query
    //   );
    // } else if (linkedCompanyId.value) {
    //   response = await twentyApi.searchOpportunitiesByCompany(
    //     linkedCompanyId.value,
    //     query
    //   );
    // } else {
    //   response = await twentyApi.searchOpportunities(query);
    // }
    response = await twentyApi.searchOpportunities(query);
    searchResults.value = response.data?.data || [];
  } catch {
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

watch(debouncedQuery, runSearch);

onMounted(() => {
  runSearch('');
});

const linkExisting = async record => {
  isLinking.value = true;
  try {
    await twentyApi.linkRecord({
      conversationId: props.conversationId,
      recordId: record.id,
      recordType: 'opportunity',
      recordName: record.name,
      recordData: {
        stage: record.subtitle,
        company: record.company,
        company_id: record.company_id,
      },
    });
    emit('linked');
  } finally {
    isLinking.value = false;
  }
};

// ─── create tab ──────────────────────────────────────────────────────────────
const form = ref({ name: '', stage: 'NEW' });
const isSaving = ref(false);
const createError = ref('');

const saveOpportunity = async () => {
  if (!form.value.name.trim()) return;
  isSaving.value = true;
  createError.value = '';
  try {
    await twentyApi.createOpportunity({
      conversationId: props.conversationId,
      name: form.value.name,
      stage: form.value.stage,
    });
    emit('linked');
  } catch {
    createError.value = t(
      'INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.OPPORTUNITY_NOT_FOUND'
    );
  } finally {
    isSaving.value = false;
  }
};

const onClose = () => emit('close');
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="
        $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.MODAL_TITLE')
      "
      :header-content="
        $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.MODAL_DESCRIPTION')
      "
    />

    <div class="flex flex-col h-auto overflow-auto">
      <div class="flex flex-col px-8 pb-4 mt-1">
        <woot-tabs
          class="ltr:[&>ul]:pl-0 rtl:[&>ul]:pr-0 h-10"
          :index="selectedTabIndex"
          @change="index => (selectedTabIndex = index)"
        >
          <woot-tabs-item
            v-for="(tab, index) in tabs"
            :key="tab.key"
            :index="index"
            :name="tab.name"
            :show-badge="false"
            is-compact
          />
        </woot-tabs>
      </div>

      <!-- Create tab -->
      <div
        v-if="selectedTabIndex === 0"
        class="flex flex-col px-4 sm:px-8 pb-4 space-y-4"
      >
        <woot-input
          v-model="form.name"
          class="w-full"
          :styles="{ ...inputStyles, padding: '0.375rem 0.75rem' }"
          :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.NAME')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.NAME')
          "
        />

        <div class="flex flex-col gap-1">
          <p class="text-sm font-medium leading-7 text-n-slate-12 mb-0">
            {{ $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.STAGE') }}
          </p>
          <select
            v-model="form.stage"
            class="w-full text-sm border border-n-weak rounded-xl px-3 py-1.5 bg-n-background focus:outline-none focus:ring-1 focus:ring-woot-500"
          >
            <option v-for="s in STAGES" :key="s" :value="s">{{ s }}</option>
          </select>
        </div>

        <p v-if="createError" class="text-xs text-ruby-500">
          {{ createError }}
        </p>

        <div
          class="flex flex-col sm:flex-row items-stretch sm:items-center justify-end w-full gap-2 mt-2"
        >
          <Button
            faded
            slate
            class="w-full sm:w-auto"
            :label="
              $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.CANCEL')
            "
            @click.prevent="onClose"
          />
          <Button
            class="w-full sm:w-auto"
            :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.SAVE')"
            :disabled="!form.name.trim() || isSaving"
            :is-loading="isSaving"
            @click.prevent="saveOpportunity"
          />
        </div>
      </div>

      <!-- Link existing tab -->
      <div v-else class="flex flex-col px-4 sm:px-8 pb-4 gap-3">
        <woot-input
          v-model="searchQuery"
          class="w-full"
          :styles="{ ...inputStyles, padding: '0.375rem 0.75rem' }"
          :placeholder="
            $t(
              'INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.LINK_SEARCH_PLACEHOLDER'
            )
          "
        />

        <div
          v-if="isSearching || isValidatingUrl"
          class="text-xs text-n-slate-11"
        >
          {{
            isValidatingUrl
              ? $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.VALIDATING')
              : $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.LOADING')
          }}
        </div>

        <p v-else-if="urlError" class="text-xs text-ruby-500">
          {{ urlError }}
        </p>

        <!-- URL-validated opportunity card -->
        <div
          v-else-if="urlValidatedOpportunity"
          class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2 px-3 py-2 rounded-xl border border-n-weak"
        >
          <div class="min-w-0 w-full sm:w-auto">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ urlValidatedOpportunity.name }}
            </p>
            <p class="text-xs text-n-slate-11 truncate">
              {{ opportunityDisplayName(urlValidatedOpportunity) }}
            </p>
          </div>
          <Button
            xs
            class="w-full sm:w-auto"
            :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.LINK_BUTTON')"
            :is-loading="isLinking"
            @click.prevent="linkExisting(urlValidatedOpportunity)"
          />
        </div>

        <ul v-else-if="searchResults.length" class="flex flex-col gap-2">
          <li
            v-for="r in searchResults"
            :key="r.id"
            class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2 px-3 py-2 rounded-xl border border-n-weak"
          >
            <div class="min-w-0 w-full sm:w-auto">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ r.name }}
              </p>
              <p class="text-xs text-n-slate-11 truncate">
                {{ opportunityDisplayName(r) }}
              </p>
            </div>
            <Button
              xs
              class="w-full sm:w-auto"
              :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.LINK_BUTTON')"
              :is-loading="isLinking"
              @click.prevent="linkExisting(r)"
            />
          </li>
        </ul>

        <div class="flex items-center justify-end w-full gap-2 mt-4">
          <Button
            faded
            slate
            class="w-full sm:w-auto"
            :label="
              $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.CANCEL')
            "
            @click.prevent="onClose"
          />
        </div>
      </div>
    </div>
  </div>
</template>
